import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, errMsg, Booking } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState } from "@/components/site/Bits";
import { BookingCard } from "@/pages/MyBookings";

export const MfaPanel = () => {
  const { user, setUser } = useAuth();
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [code, setCode] = useState("");
  if (!user) return null;
  const start = async () => setSetup((await api.post("/auth/mfa/setup")).data);
  const act = async (path: string, enabled: boolean) => {
    try {
      await api.post(`/auth/mfa/${path}`, { code });
      setUser({ ...user, mfa_enabled: enabled });
      setSetup(null);
      setCode("");
      toast.success(enabled ? "Two-factor authentication enabled" : "Two-factor authentication disabled");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-lux p-6" data-testid="mfa-panel">
      <p className="flex items-center gap-2 font-semibold"><ShieldCheck size={18} className="text-gold" /> Two-factor authentication</p>
      <p className="mt-2 text-sm text-navy/60">Status: <span data-testid="mfa-status">{user.mfa_enabled ? "Enabled" : "Not enabled"}</span></p>
      {!user.mfa_enabled && !setup && <button onClick={start} className="btn-ghost mt-4" data-testid="mfa-setup-button">Set up authenticator app</button>}
      {setup && (
        <div className="mt-4 space-y-3 text-sm">
          <p>Add this key to Google Authenticator, Authy or 1Password, then enter the 6-digit code.</p>
          <p className="break-all rounded-lg bg-stone-canvas p-3 font-mono text-xs" data-testid="mfa-secret">{setup.secret}</p>
        </div>
      )}
      {(setup || user.mfa_enabled) && (
        <div className="mt-4 flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" className="field font-mono" data-testid="mfa-code-input" />
          <button onClick={() => act(user.mfa_enabled ? "disable" : "enable", !user.mfa_enabled)} className="btn-navy" data-testid="mfa-confirm-button">{user.mfa_enabled ? "Disable" : "Enable"}</button>
        </div>
      )}
    </div>
  );
};

export default function Account() {
  const { user, logout, isStaff } = useAuth();
  const [items, setItems] = useState<Booking[] | null>(null);
  useEffect(() => {
    if (user) api.get("/me/bookings").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [user]);
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login?next=/account" replace />;
  return (
    <div>
      <PageHeader eyebrow="My account" title={`Hello, ${user.name.split(" ")[0]}`} intro={user.email} testid="account-title" />
      <div className="container-x grid gap-10 py-12 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6" data-testid="account-bookings">
          <h2 className="text-2xl">Booking history</h2>
          {!items ? <Spinner /> : items.length === 0 ? <EmptyState title="No requests yet" body="Requests you submit while signed in will appear here." cta="Browse stays" to="/stays" /> :
            items.map((b) => <BookingCard key={b.id} b={b} email={b.customer.email} onChange={(nb) => setItems(items.map((x) => (x.id === nb.id ? { ...x, ...nb } : x)))} />)}
        </div>
        <div className="space-y-6">
          {isStaff && <Link to="/admin" className="btn-gold w-full" data-testid="account-admin-link">Open admin dashboard</Link>}
          <MfaPanel />
          <button onClick={logout} className="btn-ghost w-full" data-testid="account-logout">Sign out</button>
        </div>
      </div>
    </div>
  );
}
